import React, { useMemo } from 'react';
import { Heading, Skeleton, Tag } from '@innovationnorway/cl-react';
import useOneWayIn from '../../../hooks/use-one-way-in';
import useOneWayInList from '../../../hooks/use-one-way-in-list';
import { useParams, Link } from 'react-router-dom';
import { PageParams } from '../../types/PageParams';
import { OneWayInStatus } from 'src/api/v2';
import { determineTagType, translateStatusTag } from '../../../opportunity-table/helpers/statusTags';
import { ONE_WAY_IN_DETAILS } from '../../routes';
import { truncate } from 'src/utils/string';
import { statusSort } from '../../../utils/statusSort';
import { ROUTES } from 'src/navigation';
import { useTranslation } from 'react-i18next';

const CustomerOtherOpportunities = () => {
  const { formId } = useParams<PageParams>();
  const {
    oneWayInAllSavedFormsQuery: { data: opportunityList, isLoading },
  } = useOneWayInList();
  const { oneWayInOpportunityById: opportunity } = useOneWayIn(formId);
  const { t: tOneWayIn } = useTranslation('oneWayIn');

  const filteredOpportunities = useMemo(() => {
    if (!opportunityList) return [];
    return opportunityList
      .filter(
        (opportunityItem) =>
          !!opportunityItem.organizationNumber &&
          opportunityItem.organizationNumber === opportunity.data?.organizationNumber &&
          opportunityItem.id !== opportunity.data?.id,
      )
      .sort((a, b) => statusSort(a.status?.status, b.status?.status, true));
  }, [opportunityList, opportunity]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="display--flex flex-direction--column gap--ds-2">
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      );
    }

    if (filteredOpportunities.length === 0) {
      return (
        <span>
          {tOneWayIn('opportunityDetails.NoOtherOpportunities', {
            name: opportunity.data?.organizationName || opportunity.data?.customerName,
          })}
        </span>
      );
    }

    return (
      <div className="display--flex flex-direction--column gap--ds-3">
        {filteredOpportunities.map((otherOpportunity) => (
          <div
            key={otherOpportunity.id}
            className="display--flex flex-direction--row align-items--center justify-content--space-between gap--1"
          >
            <Link to={ONE_WAY_IN_DETAILS.BASE(otherOpportunity.oneWayInId)}>
              {truncate(otherOpportunity.title, 60)}
            </Link>
            <Tag data-color={determineTagType(otherOpportunity.status?.status)}>
              {translateStatusTag(otherOpportunity.status?.status ?? OneWayInStatus.Received)}
            </Tag>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="display--flex flex-direction--column gap--ds-4 max-width--664px">
      <div className="display--flex flex-direction--row gap--ds-4">
        <Heading level={3} className="font-size--ds-5">
          {tOneWayIn('opportunityDetails.OtherOpportunitiesFrom', {
            name: opportunity.data?.organizationName || opportunity.data?.customerName,
          })}
        </Heading>
        {opportunity.data?.clusterMemberId && (
          <Link to={ROUTES.RESOURCE_BASE.MEMBERS.DETAILS(opportunity.data.clusterMemberId).BASE}>
            {tOneWayIn('opportunityDetails.ShowInCustomerRegister')}
          </Link>
        )}
      </div>
      {renderContent()}
    </div>
  );
};

export default CustomerOtherOpportunities;
